import type { FC } from "react";
import { useMemo, useState } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useOrganizationSelector } from "src/api/client-api/company/useOrganizationSelector";

export interface CompanyOption {
    "@id": string;
    id: number;
    name: string;
    [key: string]: unknown;
}

interface CompanyAutocompleteSingleProps {
    value: CompanyOption | null;
    onChange: (option: CompanyOption | null) => void;
    placeholder?: string;
    disabled?: boolean;
    excludeIds?: number[];
    sx?: object;
}

export const CompanyAutocompleteSingle: FC<CompanyAutocompleteSingleProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    excludeIds = [],
    sx = {},
}) => {
    const { t } = useTranslation();
    const { delayedSearch, setSearch } = useDelayedSearch();
    const [inputValue, setInputValue] = useState("");

    // Use server-side search with debounced search term
    const { companies = [], isLoading } = useOrganizationSelector({
        searchText: delayedSearch,
        enabled: true,
        itemsPerPage: 50,
    });

    // Filter out excluded IDs and ensure current value is always available
    const filteredCompanies = useMemo(() => {
        let filtered = companies.filter((company) => !excludeIds.includes(company.id));

        // Always include current value if it exists and isn't already in the list
        if (value && !filtered.some((company) => company["@id"] === value["@id"])) {
            filtered = [value, ...filtered];
        }

        return filtered;
    }, [companies, excludeIds, value]);

    return (
        <Autocomplete
            options={filteredCompanies}
            value={value}
            inputValue={inputValue}
            onChange={(_, newValue) => {
                onChange(newValue);
                setInputValue(""); // Clear input after selection
            }}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
                setSearch(newInputValue);
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option["@id"] === value["@id"]}
            loading={isLoading}
            disabled={disabled}
            filterOptions={(options) => options}
            noOptionsText={
                isLoading ? t("common.loading", "Loading...")
                : delayedSearch ?
                    t("common.noResults", "No results")
                :   t("common.typeToSearch", "Type to search...")
            }
            loadingText={t("common.loading", "Loading...")}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder || t("company.select.placeholder", "Select a company")}
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading && delayedSearch ?
                                    <CircularProgress color="inherit" size={20} />
                                :   null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "24px",
                            backgroundColor: "#FFFFFF",
                            height: "40px",
                            "& fieldset": {
                                borderColor: "#E7E6E4",
                            },
                            "&:hover fieldset": {
                                borderColor: "#D0D0D0",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                            },
                        },
                        "& .MuiOutlinedInput-input": {
                            fontSize: "0.875rem",
                            padding: "0px 16px !important",
                            color: "#393A36",
                            "&::placeholder": {
                                color: "#A3A2A0",
                                opacity: 1,
                            },
                        },
                        ...sx,
                    }}
                />
            )}
        />
    );
};
